import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { XCircle, CalendarClock, Mail } from "lucide-react";

const inputCls = "mt-1 w-full h-9 border border-border px-2 text-sm bg-white outline-none focus:border-ikb";
const labelCls = "text-[10px] font-mono uppercase tracking-widest text-foreground/60";

// One dialog, three bulk flows: reject (with loss reason), interview scheduling, email blast.
export default function BulkActionDialog({ action, ids, candidateIds, stages, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [roundName, setRoundName] = useState("Interview");
    const [scheduledAt, setScheduledAt] = useState("");
    const [interviewers, setInterviewers] = useState("");
    const [mode, setMode] = useState("Video");
    const [duration, setDuration] = useState("45");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [saving, setSaving] = useState(false);

    if (!action) return null;
    const n = (ids || []).length;

    const submit = async () => {
        setSaving(true);
        try {
            if (action === "reject") {
                const rejectKey = (stages || []).find((s) => s.key.toLowerCase() === "rejected")?.key || "Rejected";
                const { data } = await api.post("/applications/bulk-stage", {
                    application_ids: ids, stage: rejectKey, reject_reason: reason.trim() || null,
                });
                toast.success(`${data.moved} candidate${data.moved !== 1 ? "s" : ""} rejected`);
            } else if (action === "interview") {
                const { data } = await api.post("/interviews/bulk", {
                    application_ids: ids,
                    round_name: roundName.trim() || "Interview",
                    scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
                    interviewer_names: interviewers.split(",").map((s) => s.trim()).filter(Boolean),
                    mode,
                    duration_minutes: Number(duration) || 45,
                });
                toast.success(`${data.created} interview${data.created !== 1 ? "s" : ""} scheduled`);
            } else if (action === "email") {
                const { data } = await api.post("/messages/bulk-send", {
                    candidate_ids: candidateIds, subject: subject.trim(), body: body.trim(),
                });
                toast.success(`Sent to ${data.sent}${data.no_email ? ` · ${data.no_email} without email` : ""}`);
            }
            onDone?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const titles = {
        reject: <><XCircle className="w-4 h-4 text-rose-600" /> Reject {n} candidate{n !== 1 ? "s" : ""}</>,
        interview: <><CalendarClock className="w-4 h-4 text-ikb" /> Schedule interview · {n} candidate{n !== 1 ? "s" : ""}</>,
        email: <><Mail className="w-4 h-4 text-ikb" /> Email {n} candidate{n !== 1 ? "s" : ""}</>,
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid={`bulk-dialog-${action}`}>
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">{titles[action]}</DialogTitle>
                </DialogHeader>

                {action === "reject" && (
                    <label className="block">
                        <span className={labelCls}>Loss reason (shared for all — feeds funnel analytics)</span>
                        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                            placeholder="e.g. Budget mismatch, better internal candidate…"
                            className="mt-1 w-full border border-border px-2 py-1.5 text-sm bg-white outline-none focus:border-ikb"
                            data-testid="bulk-reject-reason" />
                    </label>
                )}

                {action === "interview" && (
                    <div className="space-y-3">
                        <label className="block">
                            <span className={labelCls}>Round name</span>
                            <input value={roundName} onChange={(e) => setRoundName(e.target.value)} className={inputCls} data-testid="bulk-interview-round" />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className={labelCls}>Date &amp; time (optional)</span>
                                <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className={inputCls} data-testid="bulk-interview-when" />
                            </label>
                            <label className="block">
                                <span className={labelCls}>Duration (min)</span>
                                <input type="number" min="15" value={duration} onChange={(e) => setDuration(e.target.value)} className={inputCls} data-testid="bulk-interview-duration" />
                            </label>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className={labelCls}>Mode</span>
                                <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls} data-testid="bulk-interview-mode">
                                    {["Video", "Phone", "Onsite", "Take-home"].map((m) => <option key={m}>{m}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className={labelCls}>Interviewers (comma-sep)</span>
                                <input value={interviewers} onChange={(e) => setInterviewers(e.target.value)} placeholder="Priya, Rahul" className={inputCls} data-testid="bulk-interview-names" />
                            </label>
                        </div>
                    </div>
                )}

                {action === "email" && (
                    <div className="space-y-3">
                        <label className="block">
                            <span className={labelCls}>Subject</span>
                            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="bulk-email-subject" />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Message</span>
                            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5}
                                className="mt-1 w-full border border-border px-2 py-1.5 text-sm bg-white outline-none focus:border-ikb"
                                data-testid="bulk-email-body" />
                        </label>
                        <p className="text-[11px] text-foreground/60">Sends a real email (Resend) and logs the message in each candidate's inbox thread.</p>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="bulk-dialog-cancel">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={saving || (action === "email" && (!subject.trim() || !body.trim()))}
                        className={action === "reject" ? "bg-rose-600 hover:bg-rose-700" : "bg-ikb hover:bg-[#001f7a]"}
                        data-testid="bulk-dialog-submit"
                    >
                        {saving ? "Working…" : action === "reject" ? `Reject ${n}` : action === "interview" ? `Schedule ${n}` : `Send ${n}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
